import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ListItemIcon,
  Box,
  ListItemText,
  SelectChangeEvent,
  styled,
} from '@mui/material';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { setCurrentTrapTable, setHubsAmount } from '../Slice/trapSlice';
import { useManageTrapModalStore } from '../Modals/AddTrapModal/ManageTrapStore';
import { useManageHubModalStore } from '../Modals/AddHubModal/ManageHubStore';

import { MLButton } from 'components/Button/Button';
import MultiCatchMouseTrapIcon from 'components/Icons/MultiCatchMouseTrapIcon';
import { TrapTableType } from 'components/TrapsTables/trapTables';
import { useAppDispatch, useAppSelector } from 'slices';
import './TablesTitle.scss';
import HubIcon from 'components/Icons/HubIcon';
import { getHubs } from 'api/traps/Hubs/getHubs';
import SnapTrapMouseIcon from 'components/Icons/SnapTrapMouseIcon';
import SnapTrapRatIcon from 'components/Icons/SnapTrapRatIcon';
import SmartRodentBaitModuleIcon from 'components/Icons/SmartRodentBaitModuleIcon';

const CustomSelect = styled(Select)({
  '&.MuiInputBase-root': {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderColor: 'red',
  },
  '& .MuiSelect-select': {
    padding: '10px 12px',
    paddingRight: '24px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #E0E0E0',
  },
});

const TablesTitle: React.FC<{ slug: string }> = ({ slug }) => {
  const { t } = useTranslation();
  const { currentTrapTable } = useAppSelector(state => state.Traps);
  const { onManageTrapModalOpen } = useManageTrapModalStore();
  const { onManageHubModalOpen } = useManageHubModalStore();
  const tables: string[] = Object.values(TrapTableType).slice(0, 5);
  const iconMap: Record<string, JSX.Element> = {
    'Multi-catch mouse traps': <MultiCatchMouseTrapIcon height={35} width={35} />,
    'Smart rodent bait modules': <SmartRodentBaitModuleIcon height={35} width={35} />,
    'Snap traps (mouse)': <SnapTrapMouseIcon height={35} width={35} />,
    'Snap traps (rat)': <SnapTrapRatIcon height={35} width={35} />,
    Hubs: <HubIcon height={35} width={35} />,
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    const fetchHubsAmount = async () => {
      try {
        const response = await getHubs(slug || '', 1);
        dispatch(setHubsAmount(response.results.length));
      } catch (error) {
        console.error('Error fetching service provider status:', error);
      }
    };

    fetchHubsAmount();
  }, [dispatch, slug]);

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    dispatch(setCurrentTrapTable({ traps: event.target.value as TrapTableType, slug }));
  };

  const imageForCurrentTable = (currentTrapTable: TrapTableType) => {
    // Відповідно до поточної таблиці виводимо відповідну іконку девайсу
    switch (currentTrapTable) {
      case TrapTableType.MultiCatchMouseTraps:
        return <MultiCatchMouseTrapIcon />;
      case TrapTableType.SnapTrapMouse:
        return <SnapTrapMouseIcon />;
      case TrapTableType.SnapTrapRat:
        return <SnapTrapRatIcon />;
      case TrapTableType.SmartRodenBaitModule:
        return <SmartRodentBaitModuleIcon />;
      case TrapTableType.Hubs:
        return <HubIcon />;
    }
  };

  const getT = (currentTrapTable: TrapTableType) => {
    switch (currentTrapTable) {
      case TrapTableType.MultiCatchMouseTraps:
        return 'TrapTables.addButtons.addTrap';
      case TrapTableType.SnapTrapMouse:
        return 'TrapTables.addButtons.addSnapTrapMouse';
      case TrapTableType.SnapTrapRat:
        return 'TrapTables.addButtons.addSnapTrapRat';
      case TrapTableType.SmartRodenBaitModule:
        return 'TrapTables.addButtons.addSRBM';
      default:
        return 'TrapTables.addButtons.addTrap';
    }
  };

  // currentTrapTable === TrapTableType.SmartRodenBaitModule ? t('TrapTables.addButtons.addBaitStation') : t('TrapTables.addButtons.addTrap')

  return (
    <div className="table-title bg-white border-bottom pb-3">
      <div className="title">
        <div className="mx-2">{imageForCurrentTable(currentTrapTable)}</div>
        <div className="d-flex align-items-center h4 mb-0 fw-bold gap-2 ">{currentTrapTable}</div>
      </div>
      <div className="buttons">
        {currentTrapTable === TrapTableType.Hubs ? (
          <MLButton
            onClick={() => onManageHubModalOpen(slug)}
            isGhost
            color="success"
            innerText={t('TrapTables.addButtons.addHub')}
          />
        ) : (
          <div className="d-flex align-items-center py-3 px-1">
            <MLButton
              onClick={() => onManageTrapModalOpen(slug, currentTrapTable)}
              isGhost
              color="success"
              innerText={t(getT(currentTrapTable))}
            />
          </div>
        )}
        <FormControl sx={{ minWidth: 120 }} size="small">
          <InputLabel
            id="demo-simple-select-standard-label"
            sx={{
              fontFamily: 'Poppins, sans-serif',
              '&.Mui-focused': {
                color: '#878a99',
              },
            }}
          >
            {t('TrapTables.CurrentSmartDevice')}
          </InputLabel>
          <CustomSelect
            value={currentTrapTable}
            onChange={handleChange}
            id="demo-simple-select-filled"
            label="Current Smart Device"
            sx={{
              width: 300,
              fontFamily: 'Poppins, sans-serif',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--vz-purple)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'var(--vz-purple)',
              },
            }}
            renderValue={selected => {
              if (typeof selected !== 'string') return null; // Ensure type safety
              return (
                <Box display="flex" alignItems="center" justifyContent="start">
                  {/* <div style={{ marginRight: '6px' }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>{iconMap[selected] || null}</ListItemIcon>
                  </div>
                  <div>
                    <ListItemText primary={selected} />
                  </div> */}
                  <ListItemText primary={selected} />
                </Box>
              );
            }}
          >
            {tables.map(tableName => (
              <MenuItem key={tableName} value={tableName} sx={{ fontFamily: 'Poppins, sans-serif' }}>
                <ListItemIcon className="mx-1">{iconMap[tableName] || null}</ListItemIcon>{' '}
                <ListItemText primary={<div className="primary-font">{tableName}</div>} />
              </MenuItem>
            ))}
          </CustomSelect>
        </FormControl>
      </div>
    </div>
  );
};

export default TablesTitle;
